import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'
import { useOptionalAuth } from '../../context/useAuth'
import { useDisplayName } from '../../hooks/useDisplayName'
import { useFormState } from '../../hooks/useFormState'
import { submitFormResponse, validateResponsePayload } from '../../services/submissionService'
import { getDesignAsset } from '../../services/designAssetService'
import { serializeDesignFromAsset } from '../../services/designEmbedService'
import { useWorkspaceMemberNames, type WorkspaceMemberDirectoryState } from '../../hooks/useWorkspaceMemberNames'
import { formatPersonAnswer } from '../../utils/forms/personDisplay'
import { useNls } from '../../nls/nlsProvider'
import type { DesignAsset } from '../../types/design'
import type { DropdownFormField, FormDefinition, FormField, FormTheme, PersonFormField } from '../../types/forms'
import type { ClientFormDefinition } from '../../types/clientEmail'
import { LogicEngine } from '../../utils/formLogicEngine'
import { AppDialog, Button, Checkbox, DatePicker, HiddenFileInput, IconButton, MultiSelect, Radio, Select, Textarea, TextInput, WorkspaceItemPicker, type WorkspaceItemReference } from '../Common'
import { DesignSnapshotRenderer } from '../Design/DesignSnapshotRenderer'
import type { FormsNlsT } from './FormsNls'
import { getFormResponderProgress, hasFormResponderInput } from './formResponderProgress'
import { registerSwitchGuard } from '../../services/workspaceSwitch/switchGuards'

const isDesignWrapped = (theme: FormTheme | null | undefined): boolean =>
  Boolean(theme && (theme.layoutVariant === 'DESIGN_WRAPPED' || theme.designAssetId || theme.heroDesignAssetId))

/**
 * Loads an optional design asset for form theming. Returns null when the id is
 * missing or the fetch fails so the renderer can fall back gracefully.
 */
const useOptionalDesignAsset = (assetId: string | null | undefined, preloaded?: DesignAsset | null) => {
  const [asset, setAsset] = useState<DesignAsset | null>(preloaded ?? null)
  useEffect(() => {
    if (preloaded !== undefined) return
    let cancelled = false
    if (!assetId) { setAsset(null); return }
    getDesignAsset(assetId)
      .then((loaded) => { if (!cancelled) setAsset(loaded) })
      .catch(() => { if (!cancelled) setAsset(null) })
    return () => { cancelled = true }
  }, [assetId, preloaded])
  return preloaded !== undefined ? preloaded : asset
}

const isFullFormDefinition = (def: FormDefinition | ClientFormDefinition): def is FormDefinition =>
  'lifecycleStatus' in def && typeof def.lifecycleStatus === 'string'

export const FormRenderer = ({
  definition,
  disabled = false,
  disableSwitchGuard = false,
  initialValues,
  onSubmitted,
  onDirtyChange,
  onSubmitAnswers,
  preloadedDesignAssets,
}: {
  definition: FormDefinition | ClientFormDefinition
  disabled?: boolean
  disableSwitchGuard?: boolean
  initialValues?: Record<string, unknown>
  onSubmitted?: (responseId: string) => void
  onDirtyChange?: (dirty: boolean) => void
  onSubmitAnswers?: (values: Record<string, unknown>) => Promise<void>
  preloadedDesignAssets?: { hero?: DesignAsset | null; background?: DesignAsset | null }
}) => {
  const { user, userProfile } = useOptionalAuth()
  const currentDisplayName = useDisplayName(userProfile ?? user)
  const { t } = useNls()
  const workspaceId = isFullFormDefinition(definition) ? definition.workspaceId : undefined
  const directoryState = useWorkspaceMemberNames(workspaceId)
  const helperUids = useMemo(
    () => new Set(directoryState.members.filter((m) => m.role === 'HELPER').map((m) => m.uid)),
    [directoryState.members],
  )
  const state = useFormState(definition, initialValues)
  const [submitError, setSubmitError] = useState('')
  const wrapped = isDesignWrapped(definition.theme)
  const heroAsset = useOptionalDesignAsset(wrapped ? definition.theme?.heroDesignAssetId ?? null : null, preloadedDesignAssets?.hero)
  const backgroundAsset = useOptionalDesignAsset(wrapped ? definition.theme?.designAssetId ?? null : null, preloadedDesignAssets?.background)
  const shellStyle = useMemo<CSSProperties>(() => ({
    ...(definition.theme?.accentColor ? { ['--form-accent' as string]: definition.theme.accentColor } : {}),
    ...(definition.theme?.backgroundColor ? { background: definition.theme.backgroundColor } : {}),
    ...(definition.theme?.textColor ? { color: definition.theme.textColor } : {}),
  }), [definition.theme?.accentColor, definition.theme?.backgroundColor, definition.theme?.textColor])
  const heroSnapshot = useMemo(
    () => (heroAsset ? serializeDesignFromAsset(heroAsset, []) : null),
    [heroAsset],
  )
  const backgroundSnapshot = useMemo(
    () => (backgroundAsset ? serializeDesignFromAsset(backgroundAsset, []) : null),
    [backgroundAsset],
  )
  const progress = getFormResponderProgress(state.steps, state.step)
  const isDirty = hasFormResponderInput(state.values)
  useEffect(() => {
    onDirtyChange?.(isDirty && !state.submitted)
  }, [isDirty, onDirtyChange, state.submitted])
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange])

  const stateReset = state.reset
  useEffect(() => {
    if (disabled || disableSwitchGuard || state.submitted || !isDirty) return
    return registerSwitchGuard({
      id: 'web-form-fill',
      describe: () => (!disabled && !disableSwitchGuard && !state.submitted && isDirty ? t('NLSWebFormFillGuardDescription', { title: definition.title }) : null),
      discard: () => {
        stateReset()
      },
    })
  }, [definition.title, disableSwitchGuard, disabled, isDirty, state.submitted, stateReset, t])
  const validateCurrentStep = () => {
    const allErrors = validateResponsePayload(definition, state.values, { helperUids })
    const visibleIds = new Set(state.stepFields.map((field) => field.id))
    const currentErrors = Object.fromEntries(Object.entries(allErrors).filter(([fieldId]) => visibleIds.has(fieldId)))
    state.setErrors(currentErrors)
    return Object.keys(currentErrors).length === 0
  }
  const submit = async () => {
    if (!validateCurrentStep()) return
    if (!user && !onSubmitAnswers) return
    setSubmitError('')
    state.setSubmitting(true)
    try {
      if (onSubmitAnswers) {
        await onSubmitAnswers(state.values)
        state.setSubmitted(true)
        onSubmitted?.('')
      } else {
        if (!user || !isFullFormDefinition(definition)) return
        const result = await submitFormResponse({
          definition,
          values: state.values,
          userId: user.uid,
          userName: currentDisplayName || user.email || 'Workspace member',
          helperUids,
        })
        if (!result.response) { state.setErrors(result.errors); return }
        state.setSubmitted(true)
        onSubmitted?.(result.response.id)
      }
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Submission failed. Please try again.')
    } finally { state.setSubmitting(false) }
  }

  const rendererClassName = `formRenderer formRenderer--live bg-[var(--surface-card)]${wrapped ? ' formRenderer--designWrapped' : ''}${definition.theme?.sectionSurfacePreset ? ` surface-${definition.theme.sectionSurfacePreset}` : ''}`
  return (
    <motion.section className={rendererClassName} style={shellStyle} aria-label={`${definition.title} response form`} animate={Object.keys(state.errors).length ? { x: [0, -7, 7, -4, 4, 0] } : { x: 0 }} transition={{ duration: 0.34 }}>
      {wrapped && heroSnapshot && (
        <div className="formRenderer__hero" aria-hidden>
          <DesignSnapshotRenderer snapshot={heroSnapshot} maxWidth={720} maxHeight={220} />
        </div>
      )}
      {wrapped && backgroundSnapshot && !heroSnapshot && (
        <div className="formRenderer__backdrop" aria-hidden>
          <DesignSnapshotRenderer snapshot={backgroundSnapshot} maxWidth={720} maxHeight={480} />
        </div>
      )}
      <AnimatePresence mode="wait">
        {state.submitted ? (
          <motion.div key="success" className="formSubmissionSuccess" initial={{ opacity: 0, scale: .88 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: .92 }}><CheckCircleRoundedIcon /><span className="formsEyebrow">Response recorded</span><h3 className="formSubmissionSuccessTitle">Submission complete</h3><p>Your response is in the workspace review queue.</p><Button variant="secondary" onClick={state.reset}>{t('NLSFormsActionSubmitAnother')}</Button></motion.div>
        ) : (
          <motion.form key={`step-${state.activeStep}`} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: .18 }} onSubmit={(event) => { event.preventDefault(); if (progress.isLastStep) void submit(); else if (validateCurrentStep()) state.setStep((current) => current + 1) }}>
            {state.steps.length > 1 && <div className="formStepProgress"><span>{progress.label}</span><div>{state.steps.map((step, index) => <i key={step} className={index <= progress.currentStepIndex ? 'isActive' : ''} />)}</div></div>}
            <div className="formLiveFields">{state.stepFields.map((field) => <motion.div key={field.id} data-orbit-target="field" data-field-id={field.id} data-orbit-title={field.label} layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, height: 0 }}><LiveField field={field} value={state.values[field.id]} required={LogicEngine.isRequired(field, definition, state.values)} error={state.errors[field.id]} disabled={disabled || state.submitting} t={t} onChange={(value) => state.setValue(field.id, value)} workspaceId={definition.workspaceId} directoryState={directoryState} /></motion.div>)}</div>
            {submitError && <motion.p className="formSubmitError" role="alert" initial={{ opacity: 0 }} animate={{ opacity: 1, x: [0, -7, 7, 0] }}>{submitError}</motion.p>}
            {!disabled && <footer className="formResponderActions">{progress.canGoBack && <Button variant="tertiary" onClick={() => state.setStep((current) => Math.max(0, current - 1))}>{t('NLSCommonActionBack')}</Button>}<Button data-orbit-target="submit-button" data-orbit-title={progress.isLastStep ? t('NLSFormsActionSubmitResponse') : t('NLSCommonActionContinue')} type="submit" variant="primary" disabled={state.submitting}>{state.submitting ? t('NLSFormsActionSubmitting') : progress.isLastStep ? t('NLSFormsActionSubmitResponse') : t('NLSCommonActionContinue')}</Button></footer>}
          </motion.form>
        )}
      </AnimatePresence>
    </motion.section>
  )
}

const LiveField = ({
  field,
  value,
  required,
  error,
  disabled,
  t,
  onChange,
  workspaceId,
  directoryState,
}: {
  field: FormField
  value: unknown
  required: boolean
  error?: string
  disabled: boolean
  t: FormsNlsT
  onChange: (value: unknown) => void
  workspaceId?: string
  directoryState?: WorkspaceMemberDirectoryState
}) => {
  const description = field.description ? <small className="formLiveDescription">{field.description}</small> : null
  const errorMessage = error ? <small>{error}</small> : null
  const heading = <span className="formLiveLabel">{field.label}{required && <em>{t('NLSFormsLabelRequired')}</em>}</span>
  if (field.type === 'TEXT' && field.multiline) return <div className={`formLiveField${error ? ' hasError' : ''}`}>{heading}{description}<AutoResizeTextarea value={String(value ?? '')} placeholder={field.placeholder} disabled={disabled} ariaLabel={field.label} error={Boolean(error)} onChange={onChange} />{errorMessage}</div>
  if (field.type === 'TEXT') return <label className={`formLiveField${error ? ' hasError' : ''}`}>{heading}{description}<TextInput value={String(value ?? '')} placeholder={field.placeholder} disabled={disabled} error={Boolean(error)} onChange={(next) => onChange(next)} />{errorMessage}</label>
  if (field.type === 'NUMBER') return <label className={`formLiveField${error ? ' hasError' : ''}`}>{heading}{description}<TextInput type="number" value={String(value ?? '')} placeholder={field.placeholder} disabled={disabled} error={Boolean(error)} min={field.validation.min} max={field.validation.max} step={field.validation.step} onChange={(next) => onChange(next === '' ? '' : Number(next))} />{errorMessage}</label>
  if (field.type === 'DATE') return <label className={`formLiveField${error ? ' hasError' : ''}`}>{heading}{description}<DatePicker value={String(value ?? '')} disabled={disabled} min={field.validation.minDate} max={field.validation.maxDate} ariaLabel={field.label} onChange={(raw) => onChange(raw)} />{errorMessage}</label>
  if (field.type === 'DROPDOWN') return <div className={`formLiveField${error ? ' hasError' : ''}`}>{heading}{description}{field.presentation === 'RADIO' && !field.allowMultiple ? <RadioGroup field={field} value={String(value ?? '')} disabled={disabled} onChange={onChange} /> : <SelectionField field={field} value={value} disabled={disabled} t={t} onChange={onChange} />}{errorMessage}</div>
  if (field.type === 'CHECKBOX') return <Checkbox wrapperClassName={`formLiveCheckbox${error ? ' hasError' : ''}`} checked={value === true} disabled={disabled} onChange={(next) => onChange(next)} label={<><span>{field.checkboxLabel ?? field.label}{required && <em>{t('NLSFormsLabelRequired')}</em>}{description}</span>{errorMessage}</>} />
  if (field.type === 'PERSON') return <PersonField field={field} value={value} required={required} disabled={disabled} error={error} t={t} onChange={onChange} workspaceId={workspaceId} directoryState={directoryState} />
  return <FileUploader field={field} value={value} disabled={disabled} error={error} t={t} onChange={onChange} />
}

const AutoResizeTextarea = ({ value, placeholder, disabled, ariaLabel, error, onChange }: { value: string; placeholder?: string; disabled: boolean; ariaLabel: string; error: boolean; onChange: (value: string) => void }) => {
  const ref = useRef<HTMLTextAreaElement>(null)
  useEffect(() => { if (ref.current) { ref.current.style.height = '0px'; ref.current.style.height = `${ref.current.scrollHeight}px` } }, [value])
  return <Textarea ref={ref} rows={2} value={value} placeholder={placeholder} disabled={disabled} error={error} aria-label={ariaLabel} onChange={onChange} />
}

const RadioGroup = ({ field, value, disabled, onChange }: { field: DropdownFormField; value: string; disabled: boolean; onChange: (value: string) => void }) => <div className="formRadioGroup" role="radiogroup" aria-label={field.label}>{field.options.map((option) => <Radio key={option.id} name={field.id} value={option.value} checked={value === option.value} disabled={disabled} onChange={() => onChange(option.value)} label={option.label} />)}</div>

const SelectionField = ({ field, value, disabled, t, onChange }: { field: DropdownFormField; value: unknown; disabled: boolean; t: FormsNlsT; onChange: (value: unknown) => void }) => {
  if (field.allowMultiple) {
    const selected = Array.isArray(value) ? value.map(String) : []
    return (
      <MultiSelect
        use="field"
        label={field.label}
        options={field.options.map((option) => ({ value: option.value, label: option.label }))}
        selectedValues={selected}
        onChange={(next: string[]) => onChange(next)}
        disabled={disabled}
      />
    )
  }
  const placeholderText = field.placeholder ?? t('NLSFormsLabelSelectOption')
  const options = [
    { value: '', label: placeholderText },
    ...field.options.map((option) => ({ value: option.value, label: option.label })),
  ]
  return (
    <Select<string>
      trigger="pill"
      ariaLabel={field.label}
      value={String(value ?? '')}
      placeholder={placeholderText}
      options={options}
      disabled={disabled}
      onChange={(val) => onChange(val)}
    />
  )
}

const FileUploader = ({ field, value, disabled, error, t, onChange }: { field: Extract<FormField, { type: 'FILE_UPLOAD' }>; value: unknown; disabled: boolean; error?: string; t: FormsNlsT; onChange: (value: File[]) => void }) => {
  const files = Array.isArray(value) ? value.filter((entry): entry is File => entry instanceof File) : []
  const choose = (fileList: FileList) => onChange(Array.from(fileList).slice(0, field.validation.maxFiles))
  return <label className={`formFileUploader${error ? ' hasError' : ''}`}><UploadFileRoundedIcon /><strong>{field.label}</strong><span>{files.length ? files.map((file) => file.name).join(', ') : t(field.validation.maxFiles === 1 ? 'NLSFormsFileChooseUpToOne' : 'NLSFormsFileChooseUpToMany', { count: field.validation.maxFiles })}</span><HiddenFileInput multiple={field.validation.maxFiles > 1} accept={field.validation.acceptedMimeTypes.join(',')} disabled={disabled} onFiles={choose} />{error && <small>{error}</small>}</label>
}

const PersonField = ({
  field,
  value,
  required,
  disabled,
  error,
  t,
  onChange,
  workspaceId,
  directoryState,
}: {
  field: PersonFormField
  value: unknown
  required: boolean
  disabled: boolean
  error?: string
  t: FormsNlsT
  onChange: (value: unknown) => void
  workspaceId?: string
  directoryState?: WorkspaceMemberDirectoryState
}) => {
  const [showPicker, setShowPicker] = useState(false)
  const fallbackDir = useWorkspaceMemberNames(directoryState ? undefined : workspaceId)
  const { memberNames, members, loaded, error: dirError } = directoryState ?? fallbackDir

  const pickerMembers = useMemo(() => {
    const filtered = field.pickScope === 'MEMBERS'
      ? members.filter((m) => m.role !== 'HELPER')
      : members
    return filtered.map((m) => ({
      id: m.uid,
      displayName: m.displayName || m.email || m.uid,
      email: m.email,
      role: m.role,
    }))
  }, [field.pickScope, members])

  const selectedIds = useMemo(() => {
    if (Array.isArray(value)) return value.map(String).filter(Boolean)
    if (typeof value === 'string' && value.trim()) return [value.trim()]
    return []
  }, [value])

  const displayNames = useMemo(() => {
    return selectedIds.map((id) =>
      formatPersonAnswer(id, memberNames, {
        formerLabel: t('NLSFormsFormerMember'),
        loading: !loaded,
      }) || (loaded ? t('NLSFormsFormerMember') : ''),
    )
  }, [selectedIds, memberNames, loaded, t])

  const handleSelect = (ref: WorkspaceItemReference) => {
    if (field.allowMultiple) {
      if (!selectedIds.includes(ref.id)) {
        onChange([...selectedIds, ref.id])
      }
    } else {
      onChange(ref.id)
      setShowPicker(false)
    }
  }

  const handleRemove = (idToRemove: string) => {
    if (field.allowMultiple) {
      onChange(selectedIds.filter((id) => id !== idToRemove))
    } else {
      onChange('')
    }
  }

  return (
    <div className={`formLiveField${error ? ' hasError' : ''}`}>
      <span className="formLiveLabel">
        {field.label}
        {required && <em>{t('NLSFormsLabelRequired')}</em>}
      </span>
      {field.description && <small className="formLiveDescription">{field.description}</small>}
      <div className="formPersonFieldControls">
        {displayNames.length > 0 ? (
          <div className="formPersonSelectedList">
            {selectedIds.map((id, index) => (
              <span key={id} className="formPersonBadge">
                <span>{displayNames[index]}</span>
                {!disabled && (
                  <IconButton
                    variant="tertiary"
                    size="sm"
                    label={t('NLSFormsActionRemoveMember')}
                    onClick={() => handleRemove(id)}
                    icon={<CloseRoundedIcon />}
                  />
                )}
              </span>
            ))}
          </div>
        ) : null}
        {dirError ? (
          <p className="formPersonStatusMsg isError">{t('NLSFormsPersonDirectoryError')}</p>
        ) : loaded && members.length === 0 ? (
          <p className="formPersonStatusMsg isEmpty">{t('NLSFormsPersonEmptyDirectory')}</p>
        ) : (!disabled || selectedIds.length === 0) ? (
          <Button
            variant="secondary"
            size="md"
            disabled={disabled}
            onClick={disabled ? undefined : () => setShowPicker(true)}
          >
            {selectedIds.length > 0 && field.allowMultiple
              ? t('NLSFormsAddMember')
              : t('NLSFormsSelectMember')}
          </Button>
        ) : null}
      </div>
      {!disabled && showPicker && (
        <AppDialog
          open={true}
          size="attachPicker"
          title={t('NLSFormsSelectMember')}
          onDismiss={() => setShowPicker(false)}
          actions={[]}
          message={
            <WorkspaceItemPicker
              workspaceId={workspaceId}
              enabledKinds={['members']}
              members={pickerMembers}
              onSelect={handleSelect}
              onClose={() => setShowPicker(false)}
            />
          }
        />
      )}
      {error && <small>{error}</small>}
    </div>
  )
}
